import {
  ANNOTATION_TOOL_KINDS,
  RESULT_INTENTS,
  currentResultIntentName,
  type AnnotationLabel,
  type AnnotationToolKind,
  type KnownResultIntent,
  type ResultSource,
  type WirePolygon,
  type WireRuler,
} from '@/backend-contract';
import type {
  ProcessingResult,
  SubmittedJobContext,
} from '@/src/processing/types';
import { resultToIntent } from '@/src/processing/engine/resultToIntent';
import {
  decodeAnnotationsFile,
  type DecodedAnnotationsFile,
} from '@/src/processing/engine/annotationsWire';
import { fetchProcessingResult } from '@/src/processing/engine/resultDownload';
import { annotationToolStore } from '@/src/processing/annotationKinds';
import { cleanUndefined, ensureError } from '@/src/utils';
import { frameOfReferenceToImageSliceAndAxis } from '@/src/utils/frameOfReference';
import { uriToDataSource } from '@/src/io/import/dataSource';
import {
  importVolumeDataSources,
  toDataSelection,
} from '@/src/io/import/importDataSources';
import { isVolumeResult } from '@/src/io/import/common';
import type { ImageMetadata } from '@/src/types/image';
import { listMasks } from '@/src/segmentation/model';
import {
  parseStatedColor,
  rejectedColorsMessage,
} from '@/src/segmentation/color';
import { useDatasetStore } from '@/src/store/datasets';
import { useDICOMStore } from '@/src/store/datasets-dicom';
import { useLayersStore } from '@/src/store/datasets-layers';
import { useSegmentationStore } from '@/src/segmentation/store';
import { useSegmentStore } from '@/src/segmentation/segments';
import { useImageCacheStore } from '@/src/store/image-cache';
import { surfaceWarning, useMessageStore } from '@/src/store/messages';
import { loadVolumeUrls } from '@/src/actions/loadUserFiles';

type ResultFile = { url: string; name: string };

type SegmentationIntent = Extract<
  KnownResultIntent,
  { intent: 'import-segmentation' }
>;
type AnnotationsIntent = Extract<
  KnownResultIntent,
  { intent: 'add-annotations' }
>;
export type ApplyIntentOutcome =
  | { status: 'applied' }
  | { status: 'failed'; error: unknown };

const sameResultSource = (
  source: ResultSource | undefined,
  target: ResultSource
): boolean =>
  source?.providerId === target.providerId &&
  source.jobId === target.jobId &&
  source.outputId === target.outputId;

function segmentResultInScene(
  target: ResultSource | undefined,
  segmentWriter: SegmentWriter
): boolean {
  if (!target) return false;
  return segmentWriter
    .resultSourcesInScene()
    .some((source) => sameResultSource(source, target));
}

// A producer may omit `source`; minting it from the submitted job and row
// gives a re-Load after a reload a receipt to recognize.
const resultSourceOf = (
  intent: SegmentationIntent | AnnotationsIntent,
  context: SubmittedJobContext | undefined
): ResultSource | undefined =>
  intent.source ??
  (context && {
    providerId: context.providerId,
    jobId: context.jobId,
    outputId: intent.id,
  });

async function loadAsImport(file: ResultFile) {
  const ds = uriToDataSource(file.url, file.name);
  const importResults = await importVolumeDataSources([ds]);
  const loaded = importResults
    .filter((r) => r.type === 'data')
    .filter(isVolumeResult);
  return loaded[0] ? toDataSelection(loaded[0]) : null;
}

// Annotation results are fully decoded and located before labels or tools are
// mutated. Store payloads remain explicit allowlists of decoded fields.

// Session-restored tools retain their result source, so that durable
// provenance doubles as an application receipt: re-Loading a job adds nothing.
function annotationResultInScene(target: ResultSource | undefined): boolean {
  if (!target) return false;
  return ANNOTATION_TOOL_KINDS.some((kind) =>
    Object.values(annotationToolStore(kind).toolByID).some(({ source }) =>
      sameResultSource(source, target)
    )
  );
}

type PreparedCore = {
  imageID: string;
  slice: number;
  frameOfReference: WireRuler['frameOfReference'];
  labelName?: string;
  frame?: number;
  name?: string;
  metadata?: Record<string, string>;
};

// Geometry travels as one opaque bag so the code below never re-branches on
// kind: only the store the bag is handed to knows its shape, and the kind table
// is what pairs the two.
type PreparedGeometry =
  | Pick<WireRuler, 'firstPoint' | 'secondPoint'>
  | Pick<WirePolygon, 'points'>;

type PreparedTool = PreparedCore & { geometry: PreparedGeometry };

type PreparedAnnotations = Record<AnnotationToolKind, PreparedTool[]>;

// Frame count of a cine target, or null for a static volume. Reads the same
// record `isCineImage` keys on; for 'cine', NumberOfSlices is the frame count.
const cineFrameCountFor = (imageID: string): number | null => {
  const info = useDICOMStore().volumeInfo[imageID];
  return info?.kind === 'cine' ? info.NumberOfSlices : null;
};

/**
 * A stored `frame` flips a tool into cine semantics everywhere (render slice,
 * visibility, jump-to drives playback), so its validity depends on the TARGET
 * image, not the producer. It stays an advisory echo the client never trusts:
 * on a static volume it is dropped, and on a cine image a frame the clip does
 * not have is dropped too, leaving the tool on every frame — the same place an
 * absent frame puts it.
 */
const prepareFrame = (
  frame: number | undefined,
  cineFrameCount: number | null
): number | undefined => {
  if (cineFrameCount == null || frame == null) return undefined;
  const inClip =
    Number.isInteger(frame) && frame >= 0 && frame < cineFrameCount;
  return inClip ? frame : undefined;
};

/**
 * Locate a wire frame of reference on THIS image, or say why it cannot be.
 * Out-of-bounds slices are accepted, matching what the renderer already places;
 * an oblique plane and a plane between slices are both unrenderable, and they
 * are distinguished so the failure names its own cause.
 */
const locateAnnotationPlane = (
  frameOfReference: WireRuler['frameOfReference'],
  imageMetadata: ImageMetadata
): { slice: number } => {
  const located = frameOfReferenceToImageSliceAndAxis(
    frameOfReference,
    imageMetadata,
    { allowOutOfBoundsSlice: true }
  );
  if (located) return located;
  // Only the non-integral slice is forgiven by the second probe, so an answer
  // here means the plane was axis-aligned all along.
  const alignedButBetweenSlices = frameOfReferenceToImageSliceAndAxis(
    frameOfReference,
    imageMetadata,
    { allowOutOfBoundsSlice: true, allowNonIntegralSlice: true }
  );
  throw new Error(
    alignedButBetweenSlices
      ? 'Annotation plane falls between slices of the input image'
      : 'Annotation plane is not aligned to an axis of the input image'
  );
};

/**
 * Project one decoded wire tool onto the store's core fields, rejecting the
 * whole result if it cannot be placed. `wire.slice` is advisory: the slice is
 * re-derived from the frame of reference against THIS image, and a plane no
 * slice of that image lies on cannot be rendered — no `slice` fallback can
 * make it otherwise.
 */
const prepareCore = (
  tool: WireRuler | WirePolygon,
  imageID: string,
  imageMetadata: ImageMetadata,
  cineFrameCount: number | null
): PreparedCore => {
  const located = locateAnnotationPlane(tool.frameOfReference, imageMetadata);
  return {
    imageID,
    slice: located.slice,
    frameOfReference: tool.frameOfReference,
    ...cleanUndefined({
      labelName: tool.labelName || undefined,
      frame: prepareFrame(tool.frame, cineFrameCount),
      name: tool.name,
      metadata: tool.metadata,
    }),
  };
};

const wireGeometry = (tool: WireRuler | WirePolygon): PreparedGeometry =>
  'points' in tool
    ? { points: tool.points }
    : { firstPoint: tool.firstPoint, secondPoint: tool.secondPoint };

const prepareAnnotations = (
  decoded: DecodedAnnotationsFile,
  imageID: string,
  imageMetadata: ImageMetadata,
  cineFrameCount: number | null
): PreparedAnnotations =>
  Object.fromEntries(
    ANNOTATION_TOOL_KINDS.map((kind) => [
      kind,
      decoded.tools[kind].map((tool) => ({
        geometry: wireGeometry(tool),
        ...prepareCore(tool, imageID, imageMetadata, cineFrameCount),
      })),
    ])
  ) as PreparedAnnotations;

// An unknown color keeps the segment's own instead of black, and is reported.
const segmentInit = (
  name: string,
  style: AnnotationLabel,
  rejected: Set<string>
) =>
  cleanUndefined({
    color: parseStatedColor(name, style.color, rejected),
    strokeWidth: style.strokeWidth,
  });

// Reported at the boundary that owns the file, naming the label and what it
// said, the way an imported config reports the same mistake.
const reportUnparseableColors = (rejected: Set<string>) => {
  const message = rejectedColorsMessage('result labels', rejected);
  if (message) useMessageStore().addError(message);
};

// Wire namespaces are per kind but bind into one registry, so the first kind to
// bind a name sets its style. Unreferenced labels stay out of the picker.
const bindReferencedSegments = (
  tools: readonly PreparedCore[],
  namespace: Record<string, AnnotationLabel>,
  rejected: Set<string>
): Record<string, string> => {
  const { segments } = useSegmentStore();
  const names = new Set(
    tools.flatMap((tool) => (tool.labelName ? [tool.labelName] : []))
  );
  return Object.fromEntries(
    [...names].map((name) => [
      name,
      segments.segmentNamed(
        name,
        segmentInit(name, namespace[name] ?? {}, rejected)
      ),
    ])
  );
};

const toolPayload = (
  { labelName, ...core }: PreparedCore,
  segmentIds: Record<string, string>,
  source: ResultSource | undefined
) => ({
  ...core,
  segmentId: (labelName && segmentIds[labelName]) || '',
  ...(source ? { source } : {}),
});

async function applyAnnotations(
  intent: AnnotationsIntent,
  parentSelection: string | undefined,
  source: ResultSource | undefined,
  fetchResult: FetchProcessingResult
): Promise<ApplyIntentOutcome> {
  if (annotationResultInScene(source)) return { status: 'applied' };

  // Tools are anchored to an image; without one they would be orphans the UI
  // never shows. Opening the file as a dataset is not a fallback either — it is
  // not an image.
  const imageCache = useImageCacheStore();
  const noImage = "Load the job's input image before applying annotations";
  if (!parentSelection || !imageCache.getImageMetadata(parentSelection)) {
    return failed(noImage);
  }

  const file = await fetchResult({
    id: intent.id,
    name: intent.name,
    url: intent.url,
  });
  const decoded = decodeAnnotationsFile(JSON.parse(await file.text()));

  const imageMetadata = imageCache.getImageMetadata(parentSelection);
  if (!imageMetadata) return failed(noImage);
  const prepared = prepareAnnotations(
    decoded,
    parentSelection,
    imageMetadata,
    cineFrameCountFor(parentSelection)
  );
  // A task that found nothing to annotate succeeded; there is just no state to add.
  if (ANNOTATION_TOOL_KINDS.every((kind) => prepared[kind].length === 0)) {
    return { status: 'applied' };
  }

  const rejectedColors = new Set<string>();
  const segmentIds = Object.fromEntries(
    ANNOTATION_TOOL_KINDS.map((kind) => [
      kind,
      bindReferencedSegments(
        prepared[kind],
        decoded.labels[kind],
        rejectedColors
      ),
    ])
  ) as Record<AnnotationToolKind, Record<string, string>>;
  reportUnparseableColors(rejectedColors);

  ANNOTATION_TOOL_KINDS.forEach((kind) => {
    const store = annotationToolStore(kind);
    prepared[kind].forEach(({ geometry, ...core }) => {
      // Held in a local so the geometry reaches the store: the registry's
      // uniform tool type does not carry the per-kind geometry keys.
      const payload = {
        ...geometry,
        ...toolPayload(core, segmentIds[kind], source),
      };
      store.addTool(payload);
    });
  });

  return { status: 'applied' };
}

type FetchProcessingResult = typeof fetchProcessingResult;

type SegmentWriter = {
  resultSourcesInScene: () => Array<ResultSource | undefined>;
  convertImageToLabelmap: ReturnType<
    typeof useSegmentationStore
  >['convertImageToLabelmap'];
};

/**
 * The download, import and scene-mutation edges, so a caller can drive the
 * intent routing without a loaded scene behind it.
 */
export type ApplyDependencies = {
  fetchResult: FetchProcessingResult;
  openVolumeUrls: typeof loadVolumeUrls;
  importVolume: (file: ResultFile) => Promise<string | null>;
  removeDataset: (selection: string) => void;
  addLayer: (
    parentSelection: string,
    childSelection: string
  ) => Promise<string | undefined>;
  segmentWriter: SegmentWriter;
};

export const appApplyDependencies = (): ApplyDependencies => ({
  fetchResult: fetchProcessingResult,
  openVolumeUrls: loadVolumeUrls,
  importVolume: loadAsImport,
  removeDataset: (selection) => useDatasetStore().remove(selection),
  addLayer: (parentSelection, childSelection) =>
    useLayersStore().addLayer(parentSelection, childSelection),
  segmentWriter: {
    resultSourcesInScene: () =>
      Object.values(useSegmentationStore().segmentations)
        .flatMap((segmentation) => listMasks(segmentation))
        .map((mask) => mask.representations.labelmap?.source),
    convertImageToLabelmap: (...args) =>
      useSegmentationStore().convertImageToLabelmap(...args),
  },
});

const failed = (message: string): ApplyIntentOutcome => ({
  status: 'failed',
  error: new Error(message),
});

async function openVolumeAsDataset(
  file: ResultFile,
  dependencies: ApplyDependencies
): Promise<ApplyIntentOutcome> {
  const datasetIds = await dependencies.openVolumeUrls({
    urls: [file.url],
    names: [file.name],
  });
  return datasetIds.length === 0
    ? failed('Result did not load')
    : { status: 'applied' };
}

async function applyLayer(
  file: ResultFile,
  parentSelection: string,
  dependencies: ApplyDependencies
): Promise<ApplyIntentOutcome> {
  const childSelection = await dependencies.importVolume(file);
  if (!childSelection) return failed('Result did not load');
  // addLayer swallows build failures and resolves undefined, so the id is the only failure signal.
  const layerId = await dependencies.addLayer(parentSelection, childSelection);
  if (layerId) return { status: 'applied' };
  dependencies.removeDataset(childSelection);
  return failed('Failed to attach layer');
}

async function applySegmentation(
  intent: SegmentationIntent,
  parentSelection: string,
  source: ResultSource | undefined,
  dependencies: ApplyDependencies
): Promise<ApplyIntentOutcome> {
  const childSelection = await dependencies.importVolume(intent);
  if (!childSelection) return failed('Result did not load');
  try {
    await dependencies.segmentWriter.convertImageToLabelmap(
      childSelection,
      parentSelection,
      { source, descriptions: intent.segments }
    );
    return { status: 'applied' };
  } finally {
    // The segmentation owns its masks; the import was only a vehicle.
    dependencies.removeDataset(childSelection);
  }
}

async function routeIntent(
  intent: KnownResultIntent,
  context: SubmittedJobContext | undefined,
  dependencies: ApplyDependencies
): Promise<ApplyIntentOutcome> {
  const parentSelection = context?.activeDatasetId;
  switch (intent.intent) {
    case 'add-base-image':
      return openVolumeAsDataset(intent, dependencies);
    case 'add-layer':
      return parentSelection
        ? applyLayer(intent, parentSelection, dependencies)
        : openVolumeAsDataset(intent, dependencies);
    case 'import-segmentation': {
      // Restored masks keep their result source, so retrying Load is
      // idempotent instead of minting duplicate segments.
      const source = resultSourceOf(intent, context);
      if (segmentResultInScene(source, dependencies.segmentWriter))
        return { status: 'applied' };
      return parentSelection
        ? applySegmentation(intent, parentSelection, source, dependencies)
        : openVolumeAsDataset(intent, dependencies);
    }
    case 'add-annotations':
      return applyAnnotations(
        intent,
        parentSelection,
        resultSourceOf(intent, context),
        dependencies.fetchResult
      );
    default: {
      const exhaustive: never = intent;
      void exhaustive;
      return failed('Unsupported result intent');
    }
  }
}

export async function applyIntent(
  intent: KnownResultIntent,
  context: SubmittedJobContext | undefined,
  dependencies: ApplyDependencies = appApplyDependencies()
): Promise<ApplyIntentOutcome> {
  try {
    return await routeIntent(intent, context, dependencies);
  } catch (error) {
    return { status: 'failed', error };
  }
}

// An unknown intent name means this client is too old; a known one means the
// producer sent a payload that fails the intent's shape.
function reportUnroutableIntent(result: ProcessingResult) {
  if (!result.intent) return;
  const name = currentResultIntentName(result.intent);
  const nameIsKnown = RESULT_INTENTS.some((known) => known === name);
  surfaceWarning(
    `Did not load ${result.name}`,
    nameIsKnown
      ? `The result intent "${result.intent}" is supported, but this result does not carry the payload that intent requires, so it was rejected. The result is still available for download in the Jobs panel.`
      : `This version cannot apply the result intent "${result.intent}". The result is still available for download in the Jobs panel.`
  );
}

export async function autoLoadProcessingResults(
  results: ProcessingResult[],
  context: SubmittedJobContext | undefined,
  dependencies: ApplyDependencies = appApplyDependencies()
): Promise<{ failedResultIds: string[] }> {
  const failedResultIds: string[] = [];
  for (const result of results) {
    const intent = resultToIntent(result);
    if (!intent) {
      reportUnroutableIntent(result);
      continue;
    }
    const outcome = await applyIntent(intent, context, dependencies);
    if (outcome.status === 'failed') {
      failedResultIds.push(result.id);
      // The completion toast already promised results.
      useMessageStore().addError(`Failed to apply ${result.name}`, {
        error: ensureError(outcome.error),
      });
      console.error(
        'Failed to auto-load processing result',
        result,
        outcome.error
      );
    }
  }
  return { failedResultIds };
}
